import { writable, get } from 'svelte/store'
import { rightSidebarSettingsStore, updateRightSidebarSettings } from './uiSettingsStore'

/**
 * Sidebar state management store
 * Handles sidebar visibility, auto-management, and user preferences
 */

export interface SidebarState {
  // Visibility state
  isCollapsed: boolean        // Whether sidebar is collapsed (hidden via transform)
  isTransitioning: boolean    // Whether sidebar is currently transitioning
  
  // Content state
  hasErrorContent: boolean    // Has grammar/spell errors
  hasInlineContent: boolean   // Has AI inline cards
  hasAIContent: boolean       // Has split view AI content
  hasTips: boolean           // Has tips to show
  
  // User preferences (persisted to localStorage)
  userPreference: 'auto' | 'always-show' | 'always-hide'
  autoOpenOnErrors: boolean
  autoOpenOnInlineCards: boolean
  rememberClosedState: boolean
  
  // UI state
  width: number              // Fixed width percentage (doesn't change)
  lastManualAction: 'open' | 'close' | null  // Track last user action
}

// Layout stabilization delay (ms)
const LAYOUT_STABILIZATION_DELAY = 200

// Load saved preferences from uiSettingsStore
function loadPreferences(): Partial<SidebarState> {
  const saved = rightSidebarSettingsStore.get()
  return {
    isCollapsed: saved.isCollapsed,
    userPreference: saved.userPreference,
    autoOpenOnErrors: saved.autoOpenOnErrors,
    autoOpenOnInlineCards: saved.autoOpenOnInlineCards,
    rememberClosedState: saved.rememberClosedState,
    width: saved.width
  }
}

// Save preferences to uiSettingsStore
function savePreferences(state: SidebarState) {
  updateRightSidebarSettings({
    isCollapsed: state.isCollapsed,
    userPreference: state.userPreference,
    autoOpenOnErrors: state.autoOpenOnErrors,
    autoOpenOnInlineCards: state.autoOpenOnInlineCards,
    rememberClosedState: state.rememberClosedState,
    width: state.width
  })
}

// Create the store
function createSidebarStore() {
  const savedPrefs = loadPreferences()
  
  const initialState: SidebarState = {
    // Start with saved collapsed state
    isCollapsed: savedPrefs.isCollapsed ?? false,
    isTransitioning: false,
    
    // Content state
    hasErrorContent: false,
    hasInlineContent: false,
    hasAIContent: false,
    hasTips: false,
    
    // User preferences with defaults
    userPreference: savedPrefs.userPreference || 'auto',
    autoOpenOnErrors: savedPrefs.autoOpenOnErrors ?? true,
    autoOpenOnInlineCards: savedPrefs.autoOpenOnInlineCards ?? true,
    rememberClosedState: savedPrefs.rememberClosedState ?? true,
    
    // Width from saved settings
    width: savedPrefs.width ?? 35,
    lastManualAction: null
  }
  
  const { subscribe, set, update } = writable<SidebarState>(initialState)
  
  return {
    subscribe,
    
    /**
     * Toggle sidebar collapsed state
     */
    toggle() {
      update(state => {
        const newState: SidebarState = {
          ...state,
          isCollapsed: !state.isCollapsed,
          isTransitioning: true,
          lastManualAction: state.isCollapsed ? 'open' : 'close'
        }
        
        // Save the new collapsed state
        savePreferences(newState)
        
        // Schedule transition end
        setTimeout(() => {
          update(s => ({ ...s, isTransitioning: false }))
          window.dispatchEvent(new CustomEvent('sidebar-transition-end'))
        }, LAYOUT_STABILIZATION_DELAY)
        
        return newState
      })
    },
    
    /**
     * Open sidebar
     */
    open(isAutoOpen = false) {
      update(state => {
        if (!state.isCollapsed) return state
        
        const newState = {
          ...state,
          isCollapsed: false,
          isTransitioning: true,
          lastManualAction: isAutoOpen ? state.lastManualAction : 'open'
        }
        
        // Save the new collapsed state if not auto-open
        if (!isAutoOpen) {
          savePreferences(newState)
        }
        
        // Schedule transition end
        setTimeout(() => {
          update(s => ({ ...s, isTransitioning: false }))
          window.dispatchEvent(new CustomEvent('sidebar-transition-end'))
        }, LAYOUT_STABILIZATION_DELAY)
        
        return newState
      })
    },
    
    /**
     * Close sidebar
     */
    close(isAutoClose = false) {
      update(state => {
        if (state.isCollapsed) return state
        
        const newState = {
          ...state,
          isCollapsed: true,
          isTransitioning: true,
          lastManualAction: isAutoClose ? state.lastManualAction : 'close'
        }
        
        // Save the new collapsed state if not auto-close
        if (!isAutoClose) {
          savePreferences(newState)
        }
        
        // Schedule transition end
        setTimeout(() => {
          update(s => ({ ...s, isTransitioning: false }))
          window.dispatchEvent(new CustomEvent('sidebar-transition-end'))
        }, LAYOUT_STABILIZATION_DELAY)
        
        return newState
      })
    },
    
    /**
     * Update content state
     */
    updateContentState(content: {
      hasErrors?: boolean
      hasInline?: boolean
      hasAI?: boolean
      hasTips?: boolean
    }) {
      update(state => {
        const newState = {
          ...state,
          hasErrorContent: content.hasErrors ?? state.hasErrorContent,
          hasInlineContent: content.hasInline ?? state.hasInlineContent,
          hasAIContent: content.hasAI ?? state.hasAIContent,
          hasTips: content.hasTips ?? state.hasTips
        }
        
        // Auto-open/close logic based on user preference
        if (state.userPreference === 'auto') {
          const hasContent = newState.hasErrorContent || newState.hasInlineContent || newState.hasAIContent
          const shouldOpen = hasContent && state.isCollapsed && 
            ((newState.hasErrorContent && state.autoOpenOnErrors) ||
             (newState.hasInlineContent && state.autoOpenOnInlineCards) ||
             newState.hasAIContent) // Always open for AI mode
          
          const shouldClose = !hasContent && !state.isCollapsed && !newState.hasTips
          
          // AI content (split view) should always open, regardless of manual close
          const forceOpenForAI = newState.hasAIContent && state.isCollapsed
          
          // Respect last manual action (but not for AI content/split view)
          if (state.rememberClosedState && state.lastManualAction === 'close' && !forceOpenForAI) {
            // Don't auto-open if user manually closed (except for AI content)
            return newState
          }
          
          if (shouldOpen || forceOpenForAI) {
            // Auto-open
            setTimeout(() => this.open(true), 100)
          } else if (shouldClose) {
            // Auto-close
            setTimeout(() => this.close(true), 100)
          }
        }
        
        return newState
      })
    },
    
    /**
     * Update user preferences
     */
    updatePreferences(prefs: Partial<Pick<SidebarState, 
      'userPreference' | 'autoOpenOnErrors' | 'autoOpenOnInlineCards' | 'rememberClosedState'
    >>) {
      update(state => {
        const newState = { ...state, ...prefs }
        savePreferences(newState)
        return newState
      })
    },
    
    /**
     * Reset to default state
     */
    reset() {
      set(initialState)
    },
    
    /**
     * Check if sidebar should be interactive
     */
    isInteractive(): boolean {
      const state = get({ subscribe })
      return !state.isCollapsed && !state.isTransitioning
    }
  }
}

export const sidebarStore = createSidebarStore()
export const sidebarActions = sidebarStore

// Helper function for safe sidebar toggle with stabilization
export function safeSidebarToggle() {
  const state = get(sidebarStore)
  
  if (state.isTransitioning) {
    // Don't toggle if already transitioning
    return
  }
  
  sidebarActions.toggle()
}